import React, { useState, useEffect, memo } from 'react';
import ReactPlayer from 'react-player';
import { Image, Spin } from 'antd';

import { DefaultVideoThumbnail } from '@/assets/img/DefaultVideoThumbnail';
import { ErrorVideoThumbnail } from '@/assets/img/ErrorVideoThumbnail';

import { CustomControls } from './CustomControls';
import { OverlayControls } from './OverlayControls';
import { useVideoPlayerContext } from './context';
import { SPINNER_SIZES } from './utils/constants';

import * as S from './styles';

export interface PlayerContentProps {
	src: string;
	poster?: string;
	thumbnail?: string;
	isDraggable?: boolean;
	withOverlay?: boolean;
	playIconSize?: number;
	lazyMountVideo?: boolean;
	actionButton?: JSX.Element;
	actionLabel?: string;
	onActionClick?: () => void;
	onPlayClick?: () => void;
	onError?: () => void;
	progressInterval?: number;
}

function PlayerContentComponent({
	src,
	poster,
	thumbnail,
	isDraggable,
	actionButton,
	actionLabel,
	playIconSize,
	withOverlay = false,
	lazyMountVideo = false,
	onActionClick,
	onPlayClick,
	onError,
	progressInterval
}: Readonly<PlayerContentProps>): JSX.Element {
	const {
		volume,
		duration,
		playbackRate,
		playedSeconds,
		playingState,
		minimized,
		fullscreen,
		controlsVisible,
		showMinimize,
		showVolume,
		seeking,
		playerRef,
		handleReady,
		handleProgress,
		handleDuration,
		handleTogglePlay,
		handleToggleSound,
		handleVolumeChange,
		handleMinimizeToStatic,
		handleMinimizeToPIP,
		handlePlaybackRateChange,
		handleToggleFullscreen,
		handleSeek
	} = useVideoPlayerContext();

	const effectivePlayingState = playingState && !seeking;

	const [isLoading, setIsLoading] = useState<boolean>(!lazyMountVideo);
	const [isError, setIsError] = useState<boolean>(false);
	const [hasStartedPlayback, setHasStartedPlayback] = useState<boolean>(false);

	const spinnerSize = isDraggable ? SPINNER_SIZES.draggable : SPINNER_SIZES.static;

	useEffect(() => {
		setIsLoading(!lazyMountVideo);
		setIsError(false);
		setHasStartedPlayback(false);
	}, [src, lazyMountVideo]);

	useEffect(() => {
		if (lazyMountVideo && hasStartedPlayback) {
			setIsLoading(true);
			setIsError(false);
		}
	}, [lazyMountVideo, hasStartedPlayback]);

	useEffect(() => {
		if (playingState && !hasStartedPlayback) {
			setHasStartedPlayback(true);
		}
	}, [playingState, hasStartedPlayback]);

	function handleReadyCallback(): void {
		setIsLoading(false);
		handleReady();
	}

	function handleErrorCallback(e: any): void {
		if (e?.name === 'AbortError') return;
		setIsLoading(false);
		setIsError(true);
		onError?.();
	}

	function renderLoadingSpinner(): JSX.Element | null {
		if (!isLoading || seeking || withOverlay) return null;

		return (
			<S.LoadingContainer $isDraggable={isDraggable} $seeking={seeking}>
				<Spin size={spinnerSize} />
			</S.LoadingContainer>
		);
	}

	function renderErrorThumbnail(): JSX.Element | null {
		if (isLoading || !isError) return null;

		return (
			<S.VideoThumbnailContainer>
				<ErrorVideoThumbnail width="100%" height="100%" />
			</S.VideoThumbnailContainer>
		);
	}

	function renderThumbnail(): JSX.Element | null {
		if (isError) return null;

		if (withOverlay && lazyMountVideo) {
			if (!isLoading && hasStartedPlayback) return null;
		} else if (isLoading || hasStartedPlayback || playedSeconds > 0) {
			return null;
		}

		return (
			<S.VideoThumbnailContainer>
				{thumbnail ? (
					<Image src={thumbnail} alt="Thumbnail" width="100%" height="100%" />
				) : (
					<DefaultVideoThumbnail width="100%" height="100%" />
				)}
			</S.VideoThumbnailContainer>
		);
	}

	const shouldShowOverlay =
		withOverlay && !isError && (!playingState || controlsVisible || (lazyMountVideo && isLoading));

	const visibleControls =
		withOverlay && lazyMountVideo ? !isLoading && playingState && controlsVisible : controlsVisible;

	return (
		<>
			{renderLoadingSpinner()}
			{renderErrorThumbnail()}
			{renderThumbnail()}
			{!lazyMountVideo || hasStartedPlayback ? (
				<ReactPlayer
					url={src}
					light={poster}
					ref={playerRef}
					volume={volume}
					duration={duration}
					minimized={minimized}
					fullscreen={fullscreen}
					isDraggable={isDraggable}
					showMinimize={showMinimize}
					visible={controlsVisible}
					onTogglePlay={handleTogglePlay}
					playedSeconds={playedSeconds}
					onToggleSound={handleToggleSound}
					onVolumeChange={handleVolumeChange}
					onToggleFullscreen={handleToggleFullscreen}
					onToggleMinimized={isDraggable ? handleMinimizeToStatic : handleMinimizeToPIP}
					onPlaybackRateChange={handlePlaybackRateChange}
					controls={false}
					playing={effectivePlayingState}
					onReady={handleReadyCallback}
					onProgress={handleProgress}
					progressInterval={progressInterval}
					onDuration={handleDuration}
					playbackRate={playbackRate}
					onError={handleErrorCallback}
				/>
			) : null}

			{!isError ? (
				<>
					{shouldShowOverlay ? (
						<OverlayControls
							isLoading={isLoading}
							spinnerSize="default"
							onPlayClick={onPlayClick}
							onActionClick={onActionClick}
							actionButton={actionButton}
							actionLabel={actionLabel}
							playIconSize={playIconSize}
						/>
					) : null}

					<CustomControls
						onSeek={handleSeek}
						volume={volume}
						duration={duration}
						minimized={minimized}
						fullscreen={fullscreen}
						playing={playingState}
						isDraggable={isDraggable}
						showMinimize={showMinimize}
						showVolume={showVolume}
						visible={visibleControls}
						playbackRate={playbackRate}
						onTogglePlay={handleTogglePlay}
						playedSeconds={playedSeconds}
						onToggleSound={handleToggleSound}
						onVolumeChange={handleVolumeChange}
						onToggleFullscreen={handleToggleFullscreen}
						onToggleMinimized={isDraggable ? handleMinimizeToStatic : handleMinimizeToPIP}
						onPlaybackRateChange={handlePlaybackRateChange}
					/>
				</>
			) : null}
		</>
	);
}

export const PlayerContent = memo(PlayerContentComponent);
